/**
 * The exercise being authored, and every named operation over it.
 *
 * **No component reads or writes this object directly.** It is the plan document's rule applied to
 * a much smaller tree for the same reason: the wizard has three steps, nine metric rows, fourteen
 * chips with two degrees, twenty-four equipment tiles and three link rows, and every one of them
 * has an invariant somebody else has to keep if the shape is the interface. A caller dispatches
 * `{ type: 'cycleMuscle', group }` and the rules about which list a group may be in stay here.
 *
 * ── What a draft is NOT ─────────────────────────────────────────────────────────────────────
 * It is not the wizard. Which step is open, whether „Jak działają metryki" is expanded, what is
 * typed in the equipment search: none of those survive a save and none of them are part of what
 * gets submitted. They live in `lib/useCreateExercise`, so this module can be read as the answer to
 * "what will the request say".
 *
 * It is not the COVER or the FILM either, past the files the trainer chose. Both are second
 * requests (`PUT /exercise/{id}/cover`, the demo film's slot → PUT → confirm) that can only be made
 * once the exercise has an id, so the draft carries the prepared files and `api/exerciseMutations`
 * decides when to send them.
 *
 * ── The three modes, and the one that drops things ──────────────────────────────────────────
 * · `blankDraft` — a fresh movement. The type's defaults are already ticked and already badged.
 * · `draftOfExercise` — editing one the trainer owns. Its metrics are adopted rather than defaulted
 *   (nothing is „auto z kategorii" on a movement that already exists) and `PatchExerciseRequest`
 *   cannot carry them anyway — see `createRequest.ts`.
 * · `draftFromSource` — duplicating. Everything the wizard can re-submit comes across; the COVER
 *   and the DEMO FILM do not. Both are server-owned media keyed to the source's id, there is no
 *   copy endpoint for either, and `PUT /exercise/{id}/cover` takes bytes rather than a reference —
 *   so a duplicate that claimed to carry them would be claiming something no request can do. The
 *   wizard says so in words rather than leaving the trainer to notice.
 *
 * The name is NOT decorated on a duplicate. „Kopia: X" is a naming convention this product has
 * nowhere else, and the trainer is standing in a wizard with the field focused.
 */

import {
  MAX_EXERCISE_LINKS,
  MAX_EXERCISE_NAME_LENGTH,
  exerciseTypeFromApi,
  muscleGroupFromApi,
  type ExerciseDetail,
  type MuscleGroup,
} from '~/entities/exercise-library';
import type { MeasurementFieldKey } from '~/shared/measurement';

import {
  adoptedMetrics,
  applyTypeDefaults,
  initialMetrics,
  toggleMetric,
  undoTypeDefaults,
  type MetricsSelection,
} from './metricsDefaults';
import { cycleMuscle, toggleMuscleDegree } from './muscleChipCycle';
import { creatableTypeOf, type CreatableExerciseType } from './exerciseType';

/** The wire's own ceilings, enforced where the trainer can see the answer. */
export const DRAFT_LIMITS = {
  /** `PostExerciseRequest.Name` — `[MaxLength(200)]`, and the counter under the field. */
  name: MAX_EXERCISE_NAME_LENGTH,
  /** `PostExerciseRequest.Description` — `[MaxLength(2000)]`. */
  description: 2000,
  /** `PostExerciseRequest.Instructions` — `[MaxItems(50)]`. */
  instructions: 50,
} as const;

/** One material as the trainer is typing it. Validated by the entity's policy, never here. */
export interface DraftLink {
  readonly url: string;
  readonly label: string;
}

/** A film ready to send: the encoded file and the duration the slot is asked for. */
export interface DraftVideo {
  readonly file: File;
  readonly durationSeconds: number;
}

export interface ExerciseDraft {
  readonly name: string;
  readonly type: CreatableExerciseType;
  readonly metrics: MetricsSelection;
  readonly primary: readonly MuscleGroup[];
  readonly secondary: readonly MuscleGroup[];
  readonly equipmentIds: readonly number[];
  readonly description: string;
  readonly instructions: readonly string[];
  readonly links: readonly DraftLink[];
  /**
   * The photograph the trainer chose in this sitting, or `null`.
   *
   * A `File`, not bytes and not a url: it is what the drop zone hands over, what `URL.createObjectURL`
   * previews and what the multipart body takes. `null` means "no new picture", which on an edit
   * leaves whatever the exercise already has.
   */
  readonly cover: File | null;
  /**
   * The demo film chosen in this sitting, already prepared (`~/shared/media/video/prepareVideo`),
   * or `null` — which on an edit leaves whatever film the exercise already has.
   */
  readonly video: DraftVideo | null;
  /**
   * The movement already EXISTS, so its metrics are frozen (R11): no toggle, no default applied by
   * a type change, and nothing about them can block a save — the PATCH cannot carry them anyway.
   * True for an edit and only an edit; a duplicate is a new movement.
   */
  readonly metricsFrozen: boolean;
  /**
   * Whether the trainer touched these in this sitting. An edit re-sends only what was touched:
   * the links on screen are the READ-filtered list (a stored link the read path refuses is not in
   * it), so re-sending them unchanged would delete that link by merely renaming the movement; and a
   * `custom` movement opens on the Siłowe tile, so re-sending the type unchanged would relabel it.
   */
  readonly linksEdited: boolean;
  readonly typeEdited: boolean;
  /**
   * The movement's stored type has no tile — `custom`, or one this build does not know — so it
   * opened on Siłowe for want of one. ANY tile press is then a choice, the Siłowe one included,
   * because pressing the tile already lit is the only way to say „it really is Siłowe".
   */
  readonly typeUnstated: boolean;
  /**
   * The movement is one Buddy proposed for a name outside the catalogue (ai-plan-builder R8), added
   * as it stands or checked here first. The create says so, and the server marks it for review
   * (exercise-library R17). Only `draftFromProposal` sets it.
   */
  readonly fromProposedExercise: boolean;
}

/** A movement nobody has authored yet. */
export function blankDraft(type: CreatableExerciseType = 'strength'): ExerciseDraft {
  return {
    name: '',
    type,
    metrics: initialMetrics(type),
    primary: [],
    secondary: [],
    equipmentIds: [],
    description: '',
    instructions: [],
    links: [],
    cover: null,
    video: null,
    metricsFrozen: false,
    linksEdited: false,
    typeEdited: false,
    typeUnstated: false,
    fromProposedExercise: false,
  };
}

/** An existing movement, opened for correction. */
export function draftOfExercise(exercise: ExerciseDetail): ExerciseDraft {
  return {
    name: exercise.name,
    type: creatableTypeOf(exercise.typeKey),
    metrics: adoptedMetrics(exercise.measurementFields),
    primary: [...exercise.primaryMuscles],
    secondary: [...exercise.secondaryMuscles],
    equipmentIds: exercise.equipment.map((piece) => piece.id),
    description: exercise.description,
    instructions: [...exercise.instructions],
    links: exercise.links.map((link) => ({ url: link.url, label: link.label })),
    cover: null,
    video: null,
    metricsFrozen: true,
    linksEdited: false,
    typeEdited: false,
    typeUnstated: exercise.typeKey === null || exercise.typeKey === 'custom',
    fromProposedExercise: false,
  };
}

/**
 * A movement seeded from another one — the „Duplikuj" path.
 *
 * Everything `draftOfExercise` takes, except the two the copy cannot inherit. See the header: the
 * cover and the demo film are server-owned media keyed to the source, and there is no request that
 * moves either onto a new row.
 *
 * The METRICS come across UNFROZEN, because the copy is a `POST` — it has no logged sets behind it,
 * so its measurement list is still the trainer's to change. They are not badged „auto z
 * kategorii": the type did not fill them in, the source did.
 */
export function draftFromSource(source: ExerciseDetail): ExerciseDraft {
  // Everything else is `draftOfExercise`'s; the difference is that nothing here is frozen, because
  // the copy does not exist yet — and a POST states every field, so "touched" means nothing on it.
  return { ...draftOfExercise(source), metricsFrozen: false };
}

/**
 * What Buddy proposed for a name outside the catalogue (ai-plan-builder R8), in the values the
 * creation takes. Structural, so the conversation's own reading of the wire is handed over as it is.
 */
export interface ProposedExerciseFields {
  /** The type as the wire names it (`Strength`); never `Custom`. */
  readonly exerciseType: string;
  /** The main muscle group as the wire names it, or `null`. */
  readonly primaryMuscle: string | null;
  readonly equipment: readonly { readonly id: number }[];
  /** Everything the movement measures, already parsed. */
  readonly measurementFields: readonly MeasurementFieldKey[];
  /** Buddy's few sentences about the movement; absent or `null` when it wrote none. */
  readonly description?: string | null;
  /** Buddy's steps, in order; absent or empty when it wrote none. */
  readonly instructions?: readonly string[];
}

/**
 * A new movement under the name Buddy wrote, filled from its proposal — the draft both „Szybkie
 * dodanie" sends as it stands and „Zweryfikuj i dodaj" opens the creator on.
 *
 * Without a proposal it is a blank movement with the name, still marked as Buddy's: the name came
 * from the conversation. The metrics are the proposal's own, adopted rather than defaulted, so
 * nothing on the first step claims „auto z kategorii" for what Buddy chose; an empty list stays
 * empty (a mobility movement measures nothing). A type or a muscle this build does not know is left
 * to the trainer: the first tile, and no muscle, rather than a guess. Buddy's description and steps
 * come across held to the wire's ceilings, and from the proposal alone: a retried quick add must send
 * exactly what the first one did, because the server recognises a repeat by them too
 * (exercise-library R17).
 */
export function draftFromProposal(name: string, proposal: ProposedExerciseFields | null): ExerciseDraft {
  const blank = blankDraft();
  const named: ExerciseDraft = {
    ...blank,
    name: name.trim().slice(0, DRAFT_LIMITS.name),
    fromProposedExercise: true,
  };
  if (proposal === null) return named;
  const primary = muscleGroupFromApi(proposal.primaryMuscle);
  return {
    ...named,
    type: creatableTypeOf(exerciseTypeFromApi(proposal.exerciseType)),
    metrics: adoptedMetrics(proposal.measurementFields),
    primary: primary === null ? [] : [primary],
    equipmentIds: [...new Set(proposal.equipment.map((piece) => piece.id).filter((id) => id > 0))],
    description: (proposal.description ?? '').slice(0, DRAFT_LIMITS.description),
    instructions: (proposal.instructions ?? []).slice(0, DRAFT_LIMITS.instructions),
  };
}

/** Every named operation the wizard's controls dispatch. */
export type ExerciseDraftAction =
  | { readonly type: 'setName'; readonly name: string }
  | { readonly type: 'setType'; readonly exerciseType: CreatableExerciseType }
  | { readonly type: 'toggleMetric'; readonly field: MeasurementFieldKey }
  | { readonly type: 'undoMetrics' }
  | { readonly type: 'cycleMuscle'; readonly group: MuscleGroup }
  | { readonly type: 'toggleMuscleDegree'; readonly group: MuscleGroup }
  | { readonly type: 'toggleEquipment'; readonly equipmentId: number }
  | { readonly type: 'setDescription'; readonly description: string }
  | { readonly type: 'addInstruction' }
  | { readonly type: 'setInstruction'; readonly at: number; readonly step: string }
  | { readonly type: 'removeInstruction'; readonly at: number }
  | { readonly type: 'addLink' }
  | { readonly type: 'setLinkUrl'; readonly at: number; readonly url: string }
  | { readonly type: 'setLinkLabel'; readonly at: number; readonly label: string }
  | { readonly type: 'removeLink'; readonly at: number }
  | { readonly type: 'setCover'; readonly file: File | null }
  | { readonly type: 'setVideo'; readonly video: DraftVideo | null };

/** Replace one entry of a list, leaving an out-of-range index alone. */
function replaceAt<T>(list: readonly T[], at: number, value: T): readonly T[] {
  if (at < 0 || at >= list.length) return list;
  return list.map((entry, index) => (index === at ? value : entry));
}

export function exerciseDraftReducer(
  draft: ExerciseDraft,
  action: ExerciseDraftAction,
): ExerciseDraft {
  switch (action.type) {
    case 'setName':
      // Sliced rather than refused: a paste past the ceiling is the case this exists for, and the
      // field's own `maxLength` already stops typing past it.
      return { ...draft, name: action.name.slice(0, DRAFT_LIMITS.name) };
    case 'setType':
      // On an edit the type is a LABEL: R11 says a change after creation re-applies no defaults,
      // and the rule lives here so no control can dispatch its way around it.
      return {
        ...draft,
        type: action.exerciseType,
        typeEdited: draft.typeEdited || draft.typeUnstated || action.exerciseType !== draft.type,
        metrics: draft.metricsFrozen
          ? draft.metrics
          : applyTypeDefaults(draft.metrics, action.exerciseType, draft.type),
      };
    case 'toggleMetric':
      return draft.metricsFrozen
        ? draft
        : { ...draft, metrics: toggleMetric(draft.metrics, action.field) };
    case 'undoMetrics':
      return { ...draft, metrics: undoTypeDefaults(draft.metrics) };
    case 'cycleMuscle':
      return { ...draft, ...cycleMuscle(draft, action.group) };
    case 'toggleMuscleDegree':
      return { ...draft, ...toggleMuscleDegree(draft, action.group) };
    case 'toggleEquipment':
      return {
        ...draft,
        equipmentIds: draft.equipmentIds.includes(action.equipmentId)
          ? draft.equipmentIds.filter((id) => id !== action.equipmentId)
          : [...draft.equipmentIds, action.equipmentId],
      };
    case 'setDescription':
      return { ...draft, description: action.description.slice(0, DRAFT_LIMITS.description) };
    case 'addInstruction':
      return draft.instructions.length >= DRAFT_LIMITS.instructions
        ? draft
        : { ...draft, instructions: [...draft.instructions, ''] };
    case 'setInstruction':
      return { ...draft, instructions: replaceAt(draft.instructions, action.at, action.step) };
    case 'removeInstruction':
      return {
        ...draft,
        instructions: draft.instructions.filter((_, index) => index !== action.at),
      };
    case 'addLink':
      // The cap is the ENTITY's, counted on distinct destinations. Here it caps the number of ROWS,
      // which is the same number while every row is a different place and is one more while two of
      // them are the same — so the control stops offering a fourth row rather than offering one the
      // save would refuse.
      return draft.links.length >= MAX_EXERCISE_LINKS
        ? draft
        : { ...draft, linksEdited: true, links: [...draft.links, { url: '', label: '' }] };
    case 'setLinkUrl': {
      const row = draft.links[action.at];
      return row === undefined
        ? draft
        : {
            ...draft,
            linksEdited: true,
            links: replaceAt(draft.links, action.at, { ...row, url: action.url }),
          };
    }
    case 'setLinkLabel': {
      const row = draft.links[action.at];
      return row === undefined
        ? draft
        : {
            ...draft,
            linksEdited: true,
            links: replaceAt(draft.links, action.at, { ...row, label: action.label }),
          };
    }
    case 'removeLink':
      return {
        ...draft,
        linksEdited: true,
        links: draft.links.filter((_, index) => index !== action.at),
      };
    case 'setCover':
      return { ...draft, cover: action.file };
    case 'setVideo':
      return { ...draft, video: action.video };
  }
}
